import { Injectable, OnDestroy } from '@angular/core';
import { IUserProfile } from '@pacifico/interfaces/inquiries-api.interface';
import { SessionStorageService } from '@pacifico/services/local/storage/storage.service';
import { SHIPPING_WAY } from '@pacifico/utils/enums';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { IResponseAuth, IUser } from '../../models/interfaces/auth-api.interface';

@Injectable({
	providedIn: 'root'
})
export class TokenService implements OnDestroy {
	private currentUserSubject: BehaviorSubject<IResponseAuth>;
	public currentUser: Observable<IResponseAuth>;
	private currentPasswordSubject: BehaviorSubject<string>;
	public currentPassword: Observable<string>;
	private orderCreatedStatus = new Subject<boolean>();
	private isLoggedInStatus = new Subject<boolean>();

	constructor(private _sessionStorage: SessionStorageService) {
		const jToken = this._sessionStorage.getItem<IResponseAuth>('token')!;
		this.currentUserSubject = new BehaviorSubject<IResponseAuth>(jToken);
		this.currentUser = this.currentUserSubject.asObservable();
		this.currentPasswordSubject = new BehaviorSubject<string>('');
		this.currentPassword = this.currentPasswordSubject.asObservable();
	}

	public get currentUserValue(): IResponseAuth {
		return this.currentUserSubject.value;
	}

	public get currentPasswordvalue(): string {
		return this.currentPasswordSubject.value;
	}

	setPasswordValue(password: string): void {
		this.currentPasswordSubject.next(password);
	}

	ngOnDestroy(): void {
		if (this.orderCreatedStatus) {
			this.orderCreatedStatus.unsubscribe();
		}
	}

	isLoggedIn(): boolean {
		return this.currentUserValue && this._sessionStorage.getItem<IResponseAuth>('token') !== null;
	}

	getLoggedInStatus(): Observable<boolean> {
		return this.isLoggedInStatus.asObservable();
	}

	setToken(user: IResponseAuth): void {
		if (user) {
			this._sessionStorage.setItem('token', user);
			this.currentUserSubject.next(user);
			this.isLoggedInStatus.next(true);
		}
	}

	setProfileAccountUser(profile: IUserProfile): void {
		const token = this._sessionStorage.getItem<IResponseAuth>('token');
		if (token !== null) {
			token.user.profile = profile;
			this._sessionStorage.setItem('token', token);
			this.currentUserSubject.next(token);
		}
	}

	updateImageProfile(image: string): void {
		const token = this._sessionStorage.getItem<IResponseAuth>('token');
		if (token !== null) {
			token.user.userImage = image;
			this._sessionStorage.setItem('token', token);
			this.currentUserSubject.next(token);
		}
	}

	getProfileAccountUser(): IUserProfile {
		return this.currentUserValue.user.profile;
	}

	getShippingDetailUser(): string {
		switch (this.currentUserValue.user.profile?.preferences) {
			case SHIPPING_WAY.MOBILE:
				return String(this.currentUserValue.user.profile.mobile);
			case SHIPPING_WAY.COORDINATE:
				return String(this.currentUserValue.user.profile.cardCoordinate);
			default:
				return this.currentUserValue.user.profile.email;
		}
	}

	getShippingDetailUserEmail(overrideEmail: string): string {
		switch (this.currentUserValue.user.profile?.preferences) {
			case SHIPPING_WAY.COORDINATE:
				return String(this.currentUserValue.user.profile.cardCoordinate);
			default:
				return overrideEmail;
		}
	}

	getShippingDetailUserPhone(overridePhone: string): string {
		switch (this.currentUserValue.user.profile?.preferences) {
			case SHIPPING_WAY.MOBILE:
				return overridePhone;
			case SHIPPING_WAY.COORDINATE:
				return String(this.currentUserValue.user.profile.cardCoordinate);
			default:
				return this.currentUserValue.user.profile.email;
		}
	}

	getShippingDetailUserPreferences(overridePreferences: string): string {
		switch (overridePreferences) {
			case SHIPPING_WAY.MOBILE:
				return String(this.currentUserValue.user.profile?.mobile);
			case SHIPPING_WAY.COORDINATE:
				return String(this.currentUserValue.user.profile?.cardCoordinate);
			default:
				return this.currentUserValue.user.profile.email;
		}
	}

	getToken(): string {
		return this.currentUserValue.token;
	}

	getSessionID(): string {
		return this.currentUserValue.sessionId;
	}

	getUser(): number {
		const username = this.currentUserValue.user.id;

		return username;
	}

	getFullUser(): IUser {
		return this.currentUserValue.user;
	}

	deleteToken(): void {
		this._sessionStorage.removeItem('token');
		//this.currentUserSubject.next();
		this.isLoggedInStatus.next(false);
		this.orderCreatedStatus.next(false);
	}
}
